import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { LockSimple, ArrowLeft } from "@phosphor-icons/react";

/**
 * AgentOpeningScreen — shared opening view for the upper-layer agents
 * (Vonzás and Értékesítés).
 *
 *   Layout:
 *     top-right pill (Aktív alap) → centered eyebrow → title → subtitle →
 *     "Válaszd ki a típust" section label → sub-card grid.
 *
 *   The Aktív alap pill replaces the previous in-page status strip. It
 *   sits in the top-right of the screen, matches the existing AgentTabHeader
 *   segmented-control surface (same height/padding/bg/border), and reveals
 *   a compact dropdown on hover with the three Foundation values.
 */
export default function AgentOpeningScreen({
  eyebrow,
  title,
  subtitle,
  options,
  columns = 4,
  // Mikromágnes-specifikus layout: ha true, a kártyák egy középre igazított
  // flex-wrap sorba kerülnek (1 kártyával is középen állnak, többel is
  // középre igazítva). Default false → változatlan grid-viselkedés.
  centerOptions = false,
  testidPrefix,
  accent,
  onSelectOption,
  controlledSelected,
  belowGrid,
  dimUnselected = false,
}) {
  const a = accent || {
    eyebrow: "#75dff5",
    iconBg: "rgba(117, 223, 245, 0.15)",
    icon: "#75dff5",
    label: "#75dff5",
    selectedBg: "rgba(117, 223, 245, 0.10)",
    selectedBorder: "rgba(117, 223, 245, 0.50)",
    selectedIconBg: "rgba(117, 223, 245, 0.22)",
  };

  const [internalSelected, setInternalSelected] = useState(null);
  const selected = controlledSelected !== undefined ? controlledSelected : internalSelected;
  const setSelected = (v) => {
    if (controlledSelected === undefined) setInternalSelected(v);
  };

  const gridCols = {
    3: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
    4: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4",
  }[columns] || "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3";

  return (
    <div className="relative" data-testid={`${testidPrefix}-opening`}>
      {/* Top-left "← Vissza" — az F3 (AgentTabHeader) fejléc-vissza pontos
          mintája: 12px, #7a7a95, ArrowLeft 13px. A main content BAL-FELSŐ
          sarkába kerül (kb. 24/24 px), NEM a középre-igazított tartalmi
          konténerbe. Így nem lebeg a hero fölött. */}
      <Link
        to="/dashboard"
        className="absolute inline-flex items-center gap-1.5 transition hover:text-white"
        style={{ top: 24, left: 24, fontSize: 12, color: "#7a7a95", zIndex: 20 }}
        data-testid={`${testidPrefix}-opening-back`}
      >
        <ArrowLeft size={13} weight="bold" />
        Vissza
      </Link>
      <div className="p-8 md:p-12 max-w-[1100px] mx-auto">
        {/* Header — centered */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="text-center"
        style={{ maxWidth: 720, margin: "0 auto" }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: a.eyebrow,
            letterSpacing: "0.11em",
            textTransform: "uppercase",
            marginBottom: 14,
          }}
        >
          {eyebrow}
        </div>
        <h1
          className="font-heading"
          style={{
            fontSize: 32,
            fontWeight: 600,
            color: "#e8e8f0",
            letterSpacing: "-0.018em",
            lineHeight: 1.15,
            marginBottom: 26,
          }}
        >
          {title}
        </h1>
        <p
          style={{
            fontSize: 15,
            color: "#9898ad",
            lineHeight: 1.7,
            maxWidth: 540,
            margin: "0 auto",
          }}
        >
          {subtitle}
        </p>
      </motion.div>

      {/* Sub-card grid section */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, delay: 0.1 }}
        className="mt-14"
      >
        <div
          className="text-center"
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: "#7c7c8a",
            letterSpacing: "0.20em",
            textTransform: "uppercase",
            marginBottom: 20,
          }}
        >
          Válaszd ki a típust
        </div>
        <div
          className={centerOptions
            ? "mx-auto flex flex-wrap justify-center gap-3.5"
            : `grid gap-3.5 mx-auto ${gridCols}`}
          style={centerOptions
            ? { maxWidth: 980 }
            : { maxWidth: columns === 3 ? 820 : 980 }}
        >
          {options.map((opt) => {
            const isSelected = selected === opt.id;
            const dimmed = dimUnselected && !isSelected;
            const card = (
              <SubCard
                key={opt.id}
                option={opt}
                selected={isSelected}
                dimmed={dimmed}
                onSelect={() => {
                  if (opt.locked) return;
                  setSelected(opt.id);
                  if (typeof onSelectOption === "function") {
                    onSelectOption(opt);
                  }
                }}
                accent={a}
                testid={`${testidPrefix}-option-${opt.id}`}
              />
            );
            // Flex módban explicit szélességű wrapper-rel csomagoljuk, hogy az
            // aspectRatio: 1.15/1 stabil kártya-méretet adjon (NEM nyúlik 100%-ra).
            if (centerOptions) {
              return (
                <div key={opt.id} style={{ width: 220, flex: "0 0 220px" }}>
                  {card}
                </div>
              );
            }
            return card;
          })}
        </div>

        {belowGrid && (
          <AnimatePresence>
            <motion.div
              key="below-grid"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.32, ease: [0.22, 0.9, 0.32, 1] }}
              className="mt-10"
            >
              {belowGrid}
            </motion.div>
          </AnimatePresence>
        )}
      </motion.div>
      </div>
    </div>
  );
}

/* ───────── SubCard — centered Brand Express style ───────── */

function SubCard({ option, selected, onSelect, accent, testid, dimmed = false }) {
  const Icon = option.icon;
  const locked = option.locked;

  const idleBg = "rgba(255,255,255,0.03)";
  const idleBorder = "rgba(255,255,255,0.07)";
  // Locked ("HAMAROSAN") kártya: kicsit erősebb kontraszt, hogy a
  // felhasználó biztosan lássa a címet + ikont, de továbbra is
  // egyértelműen inaktív (nincs hover-highlight, `not-allowed` kurzor,
  // középre igazított HAMAROSAN pill).
  const lockedBg = "rgba(255,255,255,0.045)";
  const lockedBorder = "rgba(255,255,255,0.12)";

  const bg = locked
    ? lockedBg
    : selected
    ? accent.selectedBg
    : idleBg;
  const border = locked
    ? lockedBorder
    : selected
    ? accent.selectedBorder
    : idleBorder;

  return (
    <motion.button
      type="button"
      onClick={onSelect}
      disabled={locked}
      data-testid={testid}
      aria-disabled={locked}
      whileHover={!locked && !dimmed ? { y: -2 } : undefined}
      whileTap={!locked && !dimmed ? { scale: 0.98 } : undefined}
      transition={{ duration: 0.15 }}
      className="group relative text-center rounded-xl flex flex-col items-center justify-center overflow-hidden"
      style={{
        aspectRatio: "1.15 / 1",
        background: bg,
        border: `1px solid ${border}`,
        cursor: locked || dimmed ? "not-allowed" : "pointer",
        padding: "22px 16px",
        // Locked kártya láthatósága kicsit feljebb (0.45 → 0.72), hogy a
        // cím + alcím + ikon olvashatóan jelenjenek meg, miközben
        // egyértelmű marad, hogy inaktív.
        opacity: locked ? 0.72 : (dimmed ? 0.35 : 1),
        pointerEvents: dimmed ? "none" : "auto",
        fontFamily: "inherit",
        boxShadow: selected ? `0 8px 28px -10px ${accent.icon}55` : "none",
        transition:
          "background 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease, opacity 0.32s ease",
      }}
      onMouseEnter={(e) => {
        if (!locked && !selected) {
          e.currentTarget.style.borderColor = accent.selectedBorder;
          e.currentTarget.style.background = `${accent.selectedBg.replace(/[\d.]+\)$/, "0.05)")}`;
        }
      }}
      onMouseLeave={(e) => {
        if (!locked && !selected) {
          e.currentTarget.style.borderColor = idleBorder;
          e.currentTarget.style.background = idleBg;
        }
      }}
    >
      {locked && (
        <span
          className="absolute top-2.5 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 text-[10px] tracking-[0.08em] px-2.5 py-1 rounded-full border font-medium"
          style={{
            background: "rgba(0,0,0,0.42)",
            borderColor: "rgba(255,255,255,0.18)",
            color: "#c8c8dc",
            whiteSpace: "nowrap",
          }}
        >
          <LockSimple size={10} weight="bold" /> HAMAROSAN
        </span>
      )}

      <div
        className="mb-3 flex items-center justify-center"
        style={{
          width: 44,
          height: 44,
          borderRadius: 12,
          background: selected ? accent.selectedIconBg : accent.iconBg,
          border: `1px solid ${selected ? accent.selectedBorder : "rgba(255,255,255,0.05)"}`,
          transition: "all 0.25s ease",
        }}
      >
        {Icon && (
          <Icon
            size={22}
            weight={selected ? "fill" : "regular"}
            color={accent.icon}
          />
        )}
      </div>

      <div
        className="font-heading mb-1"
        style={{
          fontSize: 17,
          fontWeight: 500,
          letterSpacing: "-0.01em",
          color: "#e8e8f0",
          lineHeight: 1.25,
        }}
      >
        {option.title}
      </div>
      <div
        style={{
          fontSize: 13,
          color: accent.label,
          letterSpacing: "0.01em",
          lineHeight: 1.3,
        }}
      >
        {option.label}
      </div>
    </motion.button>
  );
}
